import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react'
import { useAppDispatch, useAppSelector } from '@store/hooks'
import { callSocket } from '@services/websocket/socket'
import {
  fetchAllCallLogs,
  fetchAllConversations,
  setCallerInfo,
} from '@store/actions'
import { setCurrentCall, setHasActiveCall } from '@store/reducers/calls.reducer'
import { CallStatus, ICallStatus } from '@dataTypes/calls.type'
import type { ICalls } from '@dataTypes/calls.type'
import { Call } from '@twilio/voice-sdk'

const TERMINAL_CALL_UI_STATUSES: ICallStatus[] = [
  'Busy',
  'Completed',
  'Cancelled',
  'No Answer',
  'Failed',
]

/** Stops Twilio ringtone / media when socket says the call ended but the Call leg is still open. */
function hangUpLocalTwilioIfNeeded(activeCall: Call | null): void {
  if (!activeCall) return
  try {
    if (
      activeCall.direction === Call.CallDirection.Incoming &&
      (activeCall.status() === Call.State.Pending ||
        activeCall.status() === Call.State.Ringing)
    ) {
      activeCall.reject()
    } else {
      activeCall.disconnect()
    }
  } catch {
    try {
      activeCall.disconnect()
    } catch {
      console.error('Error disconnecting Twilio call')
    }
  }
}

export type ActiveCallContextValue = {
  callStatus: string
  setCallStatus: (status: string) => void
  showNumpad: boolean
  setShowNumpad: (show: boolean) => void
  callTopText: string
  setCallTopText: (text: string) => void
  callMuted: boolean
  setCallMuted: (muted: boolean) => void
  callPaused: boolean
  setCallPaused: (paused: boolean) => void
  callStartTime: number
  setCallStartTime: (t: number) => void
  callPausedTime: number
  setCallPausedTime: (t: number) => void
  callDisplayTime: string
  startCallTimer: (forceReset?: boolean) => void
  stopCallTimer: () => void
  pauseCallTimer: () => void
  conferenceSid: string | null
}

const defaultValue: ActiveCallContextValue = {
  callStatus: 'Dialing',
  setCallStatus: () => {},
  showNumpad: true,
  setShowNumpad: () => {},
  callTopText: '',
  setCallTopText: () => {},
  callMuted: false,
  setCallMuted: () => {},
  callPaused: false,
  setCallPaused: () => {},
  callStartTime: 0,
  setCallStartTime: () => {},
  callPausedTime: 0,
  setCallPausedTime: () => {},
  callDisplayTime: '',
  startCallTimer: () => {},
  stopCallTimer: () => {},
  pauseCallTimer: () => {},
  conferenceSid: null,
}

const ActiveCallContext = createContext<ActiveCallContextValue>(defaultValue)

function formatElapsed(ms: number): string {
  const totalSeconds = Math.floor(ms / 1000)
  const minutes = Math.floor(totalSeconds / 60)
  const seconds = totalSeconds % 60
  return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`
}

export function ActiveCallProvider({
  children,
}: {
  children: React.ReactNode
}): React.ReactElement {
  const dispatch = useAppDispatch()
  const callerInfo = useAppSelector((state) => state.calls.callerInfo)
  const currentCall = useAppSelector((state) => state.calls.currentCall)

  const [callStatus, setCallStatus] = useState('Dialing')
  const [showNumpad, setShowNumpad] = useState(true)
  const [callTopText, setCallTopText] = useState('')
  const [callMuted, setCallMuted] = useState(false)
  const [callPaused, setCallPaused] = useState(false)
  const [callStartTime, setCallStartTimeState] = useState(0)
  const [callPausedTime, setCallPausedTime] = useState(0)
  const [callDisplayTime, setCallDisplayTime] = useState('')
  const [conferenceSid, setConferenceSid] = useState<string | null>(null)

  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null)
  const callStartTimeRef = useRef(0)
  const callPausedTimeRef = useRef(0)
  const callerInfoRef = useRef(callerInfo)
  const currentCallRef = useRef(currentCall)
  const recordingHandlerRegisteredRef = useRef(false)
  callStartTimeRef.current = callStartTime
  callPausedTimeRef.current = callPausedTime
  callerInfoRef.current = callerInfo
  currentCallRef.current = currentCall

  const setCallStartTime = useCallback((t: number) => {
    setCallStartTimeState(t)
  }, [])

  const resetAll = useCallback(() => {
    setCallStatus('Dialing')
    setShowNumpad(true)
    setCallTopText('')
    setCallMuted(false)
    setCallPaused(false)
    setCallStartTimeState(0)
    setCallPausedTime(0)
    setCallDisplayTime('')
    setConferenceSid(null)
    if (timerRef.current) {
      clearInterval(timerRef.current)
      timerRef.current = null
    }
  }, [])

  useEffect(() => {
    if (!currentCall) {
      console.info('Resetting the active call context')
      resetAll()
    }
  }, [callerInfo, currentCall, resetAll])

  useEffect(() => {
    const callStatusHandler = (callData: {
      call_sid: string
      status: keyof typeof CallStatus
      call_id?: string
    }): void => {
      console.info('Handling call status')
      setConferenceSid(callData.call_sid)
      dispatch(fetchAllCallLogs())
      dispatch(fetchAllConversations())
      const {
        status: _status,
        call_sid: eventCallSid,
        call_id: payloadCallId,
      } = callData
      let updatedStatus = CallStatus[_status] as ICallStatus
      if (
        updatedStatus === 'Completed' &&
        callerInfoRef.current?.call_status === 'Ringing'
      ) {
        updatedStatus = CallStatus.busy as ICallStatus
      }
      const hasActiveCallInSession =
        callerInfoRef.current != null || currentCallRef.current != null
      console.info('Has active session', hasActiveCallInSession)
      console.info('Caller info', callerInfoRef.current)
      console.info('Current call', currentCallRef.current)
      const isParticipantLeave = _status === 'participant-leave'
      if (isParticipantLeave && hasActiveCallInSession) {
        hangUpLocalTwilioIfNeeded(currentCallRef.current)
        setCallStatus('Completed')
        dispatch(setHasActiveCall(false))
        dispatch(setCurrentCall(null))
      } else if (hasActiveCallInSession && updatedStatus) {
        if (TERMINAL_CALL_UI_STATUSES.includes(updatedStatus)) {
          hangUpLocalTwilioIfNeeded(currentCallRef.current)
          dispatch(setCurrentCall(null))
        }
        console.info('Has active session', hasActiveCallInSession)
        console.info('Setting call status to', updatedStatus)
        setCallStatus(updatedStatus)
        if (callerInfoRef.current) {
          const nextCallerInfo: Partial<ICalls> = {
            ...callerInfoRef.current,
            call_status: updatedStatus,
            call_id: payloadCallId,
          }
          if (payloadCallId !== undefined) {
            nextCallerInfo.call_id = payloadCallId
            nextCallerInfo.call_sid = eventCallSid
          }
          dispatch(setCallerInfo(nextCallerInfo))
        }
        if (isParticipantLeave && callerInfoRef.current?.start_date_time) {
          dispatch(setHasActiveCall(false))
          dispatch(setCurrentCall(null))
        }
      }
    }

    const callOutcomeHandler = (): void => {
      dispatch(fetchAllCallLogs())
      dispatch(fetchAllConversations())
    }
    callSocket.on('call-outcome', callOutcomeHandler)
    callSocket.on('call-status', callStatusHandler)

    const recordingHandler = (): void => {
      if (callerInfoRef.current && callerInfoRef.current.call_id) {
        console.info('Recording handler')
        dispatch(setCallerInfo(null))
      } else {
        console.info('Skipping recording handler')
      }
      dispatch(fetchAllCallLogs())
      dispatch(fetchAllConversations())
    }

    if (!recordingHandlerRegisteredRef.current) {
      callSocket.on('call-recording', recordingHandler)
      recordingHandlerRegisteredRef.current = true
    }

    const connectHandler = (): void => {
      callSocket.on('call-recording', recordingHandler)
    }
    callSocket.on('connect', connectHandler)

    callSocket.onAny((event, ...args) => {
      console.info('Received event:', event, 'with args:', args)
    })

    return () => {
      callSocket.off('call-outcome', callOutcomeHandler)
      callSocket.off('call-status', callStatusHandler)
      callSocket.off('connect', connectHandler)
      callSocket.off('call-recording', recordingHandler)
    }
  }, [dispatch])

  const startCallTimer = useCallback((forceReset?: boolean) => {
    if (timerRef.current) {
      clearInterval(timerRef.current)
      timerRef.current = null
    }
    const now = Date.now()
    if (forceReset || !callStartTimeRef.current) {
      setCallStartTimeState(now)
      callStartTimeRef.current = now
    }
    const updateDisplay = (): void => {
      if (callStartTimeRef.current) {
        const elapsed =
          Date.now() - callStartTimeRef.current + callPausedTimeRef.current
        setCallDisplayTime(formatElapsed(elapsed))
      }
    }
    updateDisplay()
    timerRef.current = setInterval(updateDisplay, 1000)
  }, [])

  const stopCallTimer = useCallback(() => {
    if (timerRef.current) {
      clearInterval(timerRef.current)
      timerRef.current = null
    }
    setCallStartTimeState(0)
    setCallPausedTime(0)
    callStartTimeRef.current = 0
    callPausedTimeRef.current = 0
    setCallDisplayTime('')
  }, [])

  const pauseCallTimer = useCallback(() => {
    if (timerRef.current && callStartTimeRef.current) {
      clearInterval(timerRef.current)
      timerRef.current = null
    }
    const additional = Date.now() - callStartTimeRef.current
    setCallPausedTime((prev) => prev + additional)
    callPausedTimeRef.current += additional
  }, [])

  const value: ActiveCallContextValue = {
    callStatus,
    setCallStatus,
    showNumpad,
    setShowNumpad,
    callTopText,
    setCallTopText,
    callMuted,
    setCallMuted,
    callPaused,
    setCallPaused,
    callStartTime,
    setCallStartTime,
    callPausedTime,
    setCallPausedTime,
    callDisplayTime,
    startCallTimer,
    stopCallTimer,
    pauseCallTimer,
    conferenceSid,
  }

  return (
    <ActiveCallContext.Provider value={value}>
      {children}
    </ActiveCallContext.Provider>
  )
}

export function useActiveCall(): ActiveCallContextValue {
  const ctx = useContext(ActiveCallContext)
  if (ctx === undefined) {
    throw new Error('useActiveCall must be used within ActiveCallProvider')
  }
  return ctx
}
